'use client'

import type {
  PropsWithChildren,
  CSSProperties,
  MouseEventHandler,
  ReactNode,
} from 'react'
import { classnames } from '@/utils/classnames'
import Image from 'next/image'
import Link from 'next/link'
import * as Icons from '../icon/icons'
import styles from './ButtonOrLink.module.scss'

type IconName = keyof typeof Icons

type ButtonVariants =
  | 'primary'
  | 'secondary'
  | 'secondaryMain'
  | 'black'
  | 'white'
  | 'red'
  | 'green'
  | 'nextQuestion'
  | 'text'
  | 'menuLink'
  | 'socials'
  | 'dark'

type SharedProps = {
  variant?: ButtonVariants
  disabled?: boolean
  classes?: string
  style?: CSSProperties
  icon?: ReactNode
  iconPosition?: 'left' | 'right'
  iconLeft?: ReactNode
  iconRight?: ReactNode
  onlyIcon?: boolean
  buttonColor?: string
  textColor?: string
  iconColor?: string
  iconSize?: number
  fontSize?: string
  /** Dla przycisków tylko z ikoną — wymagane pod kątem dostępności (czytniki ekranu) */
  ariaLabel?: string
  title?: string
  // Support for data attributes
  [key: `data-${string}`]: string | number | boolean | undefined
}

type ButtonProps = SharedProps & {
  as: 'button'
  onClick?: MouseEventHandler<HTMLButtonElement>
  type?: 'button' | 'submit' | 'reset'
  href?: never
  target?: never
  rel?: never
}

type LinkProps = SharedProps & {
  as: 'link'
  href: string
  asBlank?: boolean
  target?: string
  rel?: string
  onClick?: MouseEventHandler<HTMLAnchorElement>
  type?: never
}

type Props = PropsWithChildren<ButtonProps | LinkProps>

export function ButtonOrLink(props: Props) {
  const {
    children,
    variant = 'primary',
    disabled = false,
    classes = '',
    style = {},
    icon,
    iconPosition = 'left',
    iconLeft,
    iconRight,
    onlyIcon = false,
    buttonColor,
    textColor,
    iconColor,
    as,
    href,
    onClick,
    type,
    iconSize,
    fontSize,
    target,
    rel,
    ariaLabel,
    title,
    ...rest
  } = props

  // Extract data attributes from rest props
  const dataAttributes: Record<string, string | number | boolean> = {}
  Object.keys(rest).forEach((key) => {
    if (
      key.startsWith('data-') &&
      rest[key as keyof typeof rest] !== undefined
    ) {
      dataAttributes[key] = rest[key as keyof typeof rest] as
        | string
        | number
        | boolean
    }
  })

  const baseClasses = [styles.button, styles[variant]]
  if (onlyIcon) baseClasses.push(styles.onlyIcon)
  if (classes) baseClasses.push(classes)

  const rootClass = classnames(...baseClasses)

  const computedStyle: CSSProperties = {
    backgroundColor: buttonColor,
    color: textColor,
    fontSize: fontSize,
    ...style,
  }

  const iconStyle: CSSProperties = iconColor ? { color: iconColor } : {}

  const renderIconNode = (
    iconNode: ReactNode | string | undefined,
    position: 'left' | 'right',
  ) => {
    if (!iconNode) return null
    if (typeof iconNode === 'string' && Icons[iconNode as IconName]) {
      const IconComponent = Icons[iconNode as IconName]
      return (
        <span
          className={
            styles[
              `icon${position.charAt(0).toUpperCase() + position.slice(1)}`
            ]
          }
          style={iconStyle}
          data-icon-left={position === 'left' ? true : undefined}
          data-icon-right={position === 'right' ? true : undefined}
        >
          <IconComponent
            width={iconSize || 20}
            height={iconSize || 20}
            style={iconStyle}
          />
        </span>
      )
    }
    return (
      <span
        className={
          styles[`icon${position.charAt(0).toUpperCase() + position.slice(1)}`]
        }
        style={iconStyle}
        data-icon-left={position === 'left' ? true : undefined}
        data-icon-right={position === 'right' ? true : undefined}
      >
        {iconNode}
      </span>
    )
  }

  let content: ReactNode
  if (onlyIcon) {
    content = (
      <div className={styles.iconWrapper} style={computedStyle}>
        {icon
          ? renderIconNode(icon, iconPosition)
          : iconLeft
            ? renderIconNode(iconLeft, 'left')
            : iconRight
              ? renderIconNode(iconRight, 'right')
              : null}
      </div>
    )
  } else {
    content = (
      <span className={styles.content} style={computedStyle}>
        {iconLeft ? renderIconNode(iconLeft, 'left') : null}
        {icon && iconPosition === 'left' && renderIconNode(icon, 'left')}
        <span>{children}</span>
        {icon && iconPosition === 'right' && renderIconNode(icon, 'right')}
        {iconRight ? renderIconNode(iconRight, 'right') : null}
      </span>
    )
  }

  if (as === 'link') {
    return (
      <Link
        href={href}
        className={rootClass}
        data-variant={variant}
        target={target}
        rel={rel}
        aria-label={ariaLabel}
        title={title}
        onClick={onClick}
        {...dataAttributes}
      >
        {content}
      </Link>
    )
  }

  return (
    <button
      className={rootClass}
      disabled={disabled}
      onClick={(e) => {
        if (onClick) onClick(e)
      }}
      type={type || 'button'}
      data-variant={variant}
      aria-label={ariaLabel}
      title={title}
      {...dataAttributes}
    >
      {content}
    </button>
  )
}
